import React from 'react';
import {
  ActivityCodeChip,
  ActivityEntry
} from './activity-entry';
import './styles/index.scss';
const REVISION_VERB: Record<string, string> = {
  ADD: 'added',
  DEL: 'removed',
  MOD: 'updated'
};

export enum SystemActivityActionType {
  ASSIGN_PROFILE = 'ASSIGN_PROFILE',
  CASE_VERSION_UPDATE = 'CASE_VERSION_UPDATE',
  LATE_CASE_UPDATE = 'LATE_CASE_UPDATE'
}

export enum SystemActivityEntityType {
  WORKITEM = 'WORKITEM',
  PROCESS_INSTANCE = 'PROCESS_INSTANCE'
}

function capitalizeEntity(s: string) {
  if (!s) {
    return '';
  }
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

interface CodeLike {
  units?: string | null;
  code: string;
  transaction?: { sequenceNumber?: number | null } | null;
}

/**
 * Renders the units chip (when present), the code chip, and a trailing
 * "charge N" chip (when a transaction sequence number is present).
 * `children` are rendered between the code chip and the charge suffix.
 */
const CodeWithCharge = ({
  code,
  children
}: {
  code: CodeLike;
  children?: React.ReactNode;
}) => (
  <>
    {code.units ? <ActivityCodeChip>{code.units}</ActivityCodeChip> : null}
    <ActivityCodeChip>{code.code}</ActivityCodeChip>
    {children}
    {code.transaction?.sequenceNumber != null ? (
      <>
        {' '}
        charge{' '}
        <ActivityCodeChip>
          {String(code.transaction.sequenceNumber)}
        </ActivityCodeChip>
      </>
    ) : null}
  </>
);

const systemActivityFormatter = (activity: CMxAPI.SystemActivity): any => {
  const ts = activity.timestamp;

  if (
    activity.action === SystemActivityActionType.ASSIGN_PROFILE &&
    activity.targetEntityType === SystemActivityEntityType.WORKITEM
  ) {
    return (
      <ActivityEntry
        timestamp={ts}
        actor="System"
        primary={
          <>
            Queue matched{' '}
            <ActivityCodeChip>
              {activity?.metadata?.profileName}
            </ActivityCodeChip>{' '}
            for distribution
          </>
        }
      />
    );
  }

  if (
    (activity.action === SystemActivityActionType.CASE_VERSION_UPDATE ||
      activity.action === SystemActivityActionType.LATE_CASE_UPDATE) &&
    activity.targetEntityType === SystemActivityEntityType.PROCESS_INSTANCE
  ) {
    return (
      <ActivityEntry
        timestamp={ts}
        actor="System"
        primary={
          <>
            Case version updated to{' '}
            <ActivityCodeChip>{activity?.metadata?.caseVersion}</ActivityCodeChip>
          </>
        }
      />
    );
  }

  return (
    <ActivityEntry
      timestamp={ts}
      actor="System"
      primary={
        <>
          System activity{' '}
          <ActivityCodeChip>{activity.action}</ActivityCodeChip>
        </>
      }
    />
  );
};

const cqaActivityFormatter = (item: CQA.ActivityEntry): any => {
  const ts = item.timestamp ?? item.revInfo?.datetime;
  const actor = item.revInfo?.username ?? '';

  // Grading screen -> code activity
  if ('code' in item && !('attribute' in item) && item.code) {
    const c = item.code;
    const verb = REVISION_VERB[item.revisionType] ?? 'updated';
    const entity = capitalizeEntity(c.entityType);
    return (
      <ActivityEntry
        timestamp={ts}
        actor={actor}
        primary={
          <>
            {entity}{' '}
            <CodeWithCharge code={c}>{verb}</CodeWithCharge>
          </>
        }
      />
    );
  }

  // Grading screen -> legacy assessment on codes
  if ('codeAssessment' in item && item.codeAssessment) {
    const ca = item.codeAssessment;
    const parent = ca.parentCode;
    const verb = REVISION_VERB[item.revisionType] ?? 'updated';
    return (
      <ActivityEntry
        timestamp={ts}
        actor={actor}
        primary={
          <>
            Assessment <ActivityCodeChip>{ca.assessment}</ActivityCodeChip>{' '}
            {verb} role <ActivityCodeChip>{ca.role}</ActivityCodeChip>
            {parent ? (
              <>
                {' '}
                for {capitalizeEntity(parent.entityType)}{' '}
                <CodeWithCharge code={parent} />
              </>
            ) : (
              <> n/a</>
            )}
          </>
        }
      />
    );
  }

  // Grading screen -> attributes on codes
  if ('attribute' in item
    && item.attribute
    && item.code
    && (item.attribute.entityType === 'CODE_PROCEDURE'
      || item.attribute.entityType === 'CODE_DIAGNOSIS'
      || item.attribute.entityType === 'CODE_MODIFIER')
    ) {
    const attr = item.attribute;
    const code = item.code;
    const verb = REVISION_VERB[item.revisionType] ?? 'updated';
    return (
      <ActivityEntry
        timestamp={ts}
        actor={actor}
        primary={
          <>
            Question <ActivityCodeChip>{attr?.questionConfiguration?.text ?? attr.questionLabel}</ActivityCodeChip>{' '}
            {verb} answered <ActivityCodeChip>{attr.answerDisplayText}</ActivityCodeChip>
            {' '}
            {' '}
            for {capitalizeEntity(code.entityType)}{' '}
            <CodeWithCharge code={code} />
          </>
        }
      />
    );
  }

  // loose spans
  if (item.entityType == 'span' && 'span' in item) {
    const s = item.span;
    const verb = REVISION_VERB[item.revisionType] ?? 'updated';
    const isImportedDelete = s.original && item.revisionType === 'DEL';
    const entityLabel = isImportedDelete ? 'Imported span' : 'Loose span';
    return (
      <ActivityEntry
        timestamp={ts}
        actor={actor}
        primary={
          <>
            {entityLabel}
            {
              s.coveredText
              ?  <><ActivityCodeChip>{s.coveredText}</ActivityCodeChip> {verb}</>
              : ` ${verb} start offset ${s.beginOffset} end offset ${s.endOffset}`
            }
          </>
        }
      />
    );
  }

  // Grading screen -> case question
  if ('attribute' in item && item.attribute?.entityType === 'CASE') {
    const attr = item.attribute;
    const verb = REVISION_VERB[item.revisionType] ?? 'updated';
    return (
      <ActivityEntry
        timestamp={ts}
        actor={actor}
        primary={
          <>
            Case question <ActivityCodeChip>{attr?.questionConfiguration?.text ?? attr.questionLabel}</ActivityCodeChip>{' '}
            {verb} answered <ActivityCodeChip>{attr.answerDisplayText}</ActivityCodeChip>
          </>
        }
      />
    );
  }

  // Span popover -> span highlight
  if ('attribute' in item && item.attribute?.entityType === 'SPAN') {
    const attr = item.attribute;
    const s = item.span;
    const verb = REVISION_VERB[item.revisionType] ?? 'updated';
    return (
      <ActivityEntry
        timestamp={ts}
        actor={actor}
        primary={
          <>
            Span highlight question <ActivityCodeChip>{attr?.questionConfiguration?.text ?? attr.questionLabel}</ActivityCodeChip>{' '}
            {verb} answered <ActivityCodeChip>{attr.answerDisplayText}</ActivityCodeChip>
          </>
        }
      />
    );
  }

  // Grading screen -> code attached to a span
  if ('codeSpan' in item && item.codeSpan) {
    const cs = item.codeSpan;
    // Only surface original (imported) code spans when they're deleted — adds/updates
    // to imported attachments are noise.
    if (cs.original && item.revisionType !== 'DEL') {
      return false;
    }
    const verb = REVISION_VERB[item.revisionType] ?? 'updated';
    return (
      <ActivityEntry
        timestamp={ts}
        actor={actor}
        primary={
          <>
            {capitalizeEntity(cs.codeType)} <ActivityCodeChip>{cs.code?.code}</ActivityCodeChip>{' '}
            {verb} attached to span{' '}
            {
              cs.span?.coveredText
              ? <ActivityCodeChip>{cs.span.coveredText}</ActivityCodeChip>
              : ` start offset ${cs.span?.beginOffset} end offset ${cs.span?.endOffset}`
            }
          </>
        }
      />
    );
  }

  console.log("myLog item", item);
  return false;
};

export { systemActivityFormatter, cqaActivityFormatter };
